import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = process.env.REACT_APP_API_URL || '/api';

const FLAGS = {
  FRA:'🇫🇷',ESP:'🇪🇸',ENG:'🏴󠁧󠁢󠁥󠁮󠁧󠁿',GER:'🇩🇪',POR:'🇵🇹',NED:'🇳🇱',BEL:'🇧🇪',
  CRO:'🇭🇷',AUT:'🇦🇹',SUI:'🇨🇭',SCO:'🏴󠁧󠁢󠁳󠁣󠁴󠁿',TUR:'🇹🇷',NOR:'🇳🇴',SWE:'🇸🇪',
  CZE:'🇨🇿',BIH:'🇧🇦',ARG:'🇦🇷',BRA:'🇧🇷',USA:'🇺🇸',MEX:'🇲🇽',CAN:'🇨🇦',
  URU:'🇺🇾',COL:'🇨🇴',ECU:'🇪🇨',PAR:'🇵🇾',PAN:'🇵🇦',HAI:'🇭🇹',CUW:'🇨🇼',
  MAR:'🇲🇦',SEN:'🇸🇳',EGY:'🇪🇬',NGA:'🇳🇬',CIV:'🇨🇮',GHA:'🇬🇭',RSA:'🇿🇦',
  TUN:'🇹🇳',ALG:'🇩🇿',CMR:'🇨🇲',COD:'🇨🇩',JPN:'🇯🇵',KOR:'🇰🇷',IRN:'🇮🇷',
  SAU:'🇸🇦',AUS:'🇦🇺',QAT:'🇶🇦',UZB:'🇺🇿',NZL:'🇳🇿',IRQ:'🇮🇶',JOR:'🇯🇴',CPV:'🇨🇻',
};

function Flag({ code, size = 40 }) {
  return <span style={{fontSize: size, lineHeight:1, display:'block'}}>{FLAGS[code] || '🏳'}</span>;
}

function ForecastCard({ match }) {
  const [fc, setFc] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (!open && !fc) {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/matches/${match.id}/forecast`);
        setFc(data.forecast);
      } finally { setLoading(false); }
    }
    setOpen(o => !o);
  };

  const kickoff = match.kickoff ? parseISO(match.kickoff) : null;
  const dateStr = kickoff ? format(kickoff, "EEE d MMM · HH'h'mm", { locale: fr }) : '';

  return (
    <div className="card" style={{marginBottom:12,overflow:'visible'}}>
      <div className="match-card" onClick={toggle} style={{cursor:'pointer'}}>
        <div className="match-team">
          <Flag code={match.home.code} size={44} />
          <span className="team-name">{match.home.name}</span>
        </div>

        {/* Centre */}
        <div className="match-center">
          <div className="match-vs">VS</div>
          <div className="match-time">{dateStr} UTC</div>
          <span className="badge badge-soon">{match.stage}</span>
        </div>

        <div className="match-team">
          <Flag code={match.away.code} size={44} />
          <span className="team-name">{match.away.name}</span>
        </div>
      </div>

      {/* Aperçu cotes condensé toujours visible */}
      {!open && fc && (
        <div style={{padding:'8px 24px 14px',display:'flex',gap:8}}>
          <div className="odd-box" style={{flex:1,padding:'8px 6px'}}>
            <div className="odd-label">1</div>
            <div className="odd-value" style={{fontSize:'1.2rem'}}>{fc.odd_home}</div>
          </div>
          <div className="odd-box" style={{flex:1,padding:'8px 6px'}}>
            <div className="odd-label">X</div>
            <div className="odd-value" style={{fontSize:'1.2rem'}}>{fc.odd_draw}</div>
          </div>
          <div className="odd-box" style={{flex:1,padding:'8px 6px'}}>
            <div className="odd-label">2</div>
            <div className="odd-value" style={{fontSize:'1.2rem'}}>{fc.odd_away}</div>
          </div>
        </div>
      )}

      {open && (
        <div style={{padding:'0 24px 24px'}}>
          {loading ? <div className="spinner" style={{margin:'20px auto'}} /> : fc ? (
            <>
              {/* Barres probabilités */}
              <div style={{marginBottom:16}}>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'var(--muted)',marginBottom:6}}>
                  <span>{match.home.name} {fc.p_home}%</span>
                  <span>Nul {fc.p_draw}%</span>
                  <span>{fc.p_away}% {match.away.name}</span>
                </div>
                <div style={{display:'flex',borderRadius:8,overflow:'hidden',height:12,gap:2}}>
                  <div style={{width:`${fc.p_home}%`,background:'var(--accent2)',borderRadius:'8px 0 0 8px'}} />
                  <div style={{width:`${fc.p_draw}%`,background:'var(--muted)'}} />
                  <div style={{width:`${fc.p_away}%`,background:'var(--danger)',borderRadius:'0 8px 8px 0'}} />
                </div>
              </div>

              {/* Cotes */}
              <div className="odds-grid" style={{marginBottom:16}}>
                <div className="odd-box">
                  <div className="odd-label">Victoire {match.home.name}</div>
                  <div className="odd-pct">{fc.p_home}%</div>
                  <div className="odd-value">{fc.odd_home}</div>
                </div>
                <div className="odd-box">
                  <div className="odd-label">Match nul</div>
                  <div className="odd-pct">{fc.p_draw}%</div>
                  <div className="odd-value">{fc.odd_draw}</div>
                </div>
                <div className="odd-box">
                  <div className="odd-label">Victoire {match.away.name}</div>
                  <div className="odd-pct">{fc.p_away}%</div>
                  <div className="odd-value">{fc.odd_away}</div>
                </div>
              </div>

              {/* Score probable + ELO */}
              <div style={{
                background:'var(--bg3)',border:'1px solid var(--border)',
                borderRadius:10,padding:'14px 16px',
                display:'flex',justifyContent:'space-between',alignItems:'center',
                flexWrap:'wrap',gap:12
              }}>
                <div>
                  <div style={{fontSize:11,color:'var(--muted)',marginBottom:4}}>SCORE LE PLUS PROBABLE</div>
                  <div style={{fontFamily:'var(--font-head)',fontSize:'1.8rem',letterSpacing:3,color:'var(--accent)'}}>
                    {fc.best_score.home} — {fc.best_score.away}
                  </div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:11,color:'var(--muted)',marginBottom:4}}>ELO</div>
                  <div style={{fontSize:13}}>
                    <span style={{color:'var(--accent2)'}}>{match.home.name} {fc.elo_home}</span>
                    {' vs '}
                    <span style={{color:'var(--danger)'}}>{fc.elo_away} {match.away.name}</span>
                  </div>
                  <div style={{fontSize:11,color:'var(--muted)'}}>
                    Buts attendus : {fc.lambda_home} – {fc.lambda_away}
                  </div>
                </div>
              </div>

              <p style={{fontSize:11,color:'var(--muted)',marginTop:12,textAlign:'center'}}>
                ⚠️ Prédiction algorithmique basée sur les ELO — pas un conseil de pari
              </p>
            </>
          ) : null}
        </div>
      )}

      <div className="match-card-footer" style={{cursor:'pointer'}} onClick={toggle}>
        <span style={{fontSize:12,color:'var(--muted)'}}>
          {open ? '▲ Masquer la prédiction' : '▼ Voir la prédiction & les cotes'}
        </span>
      </div>
    </div>
  );
}

export default function ForecastPage() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/matches?status=upcoming&limit=30`)
      .then(r => setMatches(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="container" style={{padding:'32px 20px'}}>
      <h2 className="section-title">🔮 Prédictions <span>&amp;</span> Cotes</h2>
      <p style={{color:'var(--muted)',marginBottom:24,fontSize:14}}>
        Cotes calculées via modèle ELO + distribution de Poisson. Marge bookmaker simulée à 5%.
      </p>
      {loading ? <div className="spinner" /> : (
        matches.length === 0
          ? <div className="empty">Aucun match à venir</div>
          : matches.map(m => <ForecastCard key={m.id} match={m} />)
      )}
    </div>
  );
}
